import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useFormikContext } from 'formik';
import { debounce, isEqual } from 'lodash';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Spinner from '@dashboard/components/Spinner/Spinner';

const AutoSave = ({ debounceMs = 0, showState = true }) => {
  const formik = useFormikContext();

  const [initialHasChanges, setInitialHasChanges] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const previousValuesRef = useRef(formik.values);
  const previousInitialValuesRef = useRef(formik.initialValues);
  const isSubmittingRef = useRef(false);
  const hasChangedSinceSubmitRef = useRef(false);

  useEffect(() => {
    // Check if initialValues changed (form reinitialized)
    const initialValuesChanged = !isEqual(formik.initialValues, previousInitialValuesRef.current);

    if (initialValuesChanged) {
      // Form was reinitialized - reset state if we just submitted
      if (isSubmittingRef.current) {
        setHasChanges(false);
        hasChangedSinceSubmitRef.current = false;
        isSubmittingRef.current = false;
      }
      previousInitialValuesRef.current = formik.initialValues;
      previousValuesRef.current = formik.values;
      return;
    }

    // Track whether values have changed since last submit
    if (formik.values !== previousValuesRef.current) {
      hasChangedSinceSubmitRef.current = true;
      setHasChanges(true);
      previousValuesRef.current = formik.values;
    }
  }, [formik.values, formik.initialValues]);

  const debouncedSubmit = useCallback(
    debounce(async () => {
      isSubmittingRef.current = true;
      return formik.submitForm().then(success => {
        if (success) {
          // Don't reset hasChanges here - let the reinitialization effect handle it
          // setHasChanges(false);
        } else {
          isSubmittingRef.current = false;
        }
      });
    }, debounceMs),
    [formik.submitForm, debounceMs],
  );

  useEffect(() => {
    // Instead of formik.dirty, use the local hasChanges state
    if (hasChanges) {
      debouncedSubmit();
    }

    // Cleanup the debounce on unmount to prevent late invocations
    return () => {
      debouncedSubmit.cancel();
    };
  }, [debouncedSubmit, hasChanges, formik.values]);

  useEffect(() => {
    if (hasChanges) {
      setInitialHasChanges(true);
    }
  }, [hasChanges]);

  if (!showState) return null;

  if (!initialHasChanges) return null;

  // Show the saving state
  if (formik.isSubmitting) {
    return (
      <div className="flex justify-end items-center text-gray-500 dark:text-muted-foreground">
        <Spinner size="small" />
        <span className="ml-2">Saving changes</span>
      </div>
    );
  }

  // Show the saved state
  if (!hasChanges && !formik.isSubmitting) {
    return (
      <div className="flex justify-end items-center text-gray-500 dark:text-muted-foreground">
        <CheckCircleIcon className="h-5 w-5 text-green-300" />
        <span className="ml-2">Changes saved</span>
      </div>
    );
  }

  // Show the unsaved state
  if (hasChanges || !formik.isValid) {
    return (
      <div className="flex justify-end items-center text-gray-500 dark:text-muted-foreground">
        <>Changes not saved</>
      </div>
    );
  }

  return null;
};

export default AutoSave;
